package utils

/**
 * SessionStorage utility functions for browser session storage.
 * SessionStorage is cleared when the browser tab/window is closed.
 */

/**
 * Get an item from SessionStorage
 * @param key The key to retrieve
 * @return The value as a string, or null if not found
 */
fun getSessionItem(key: String): String? {
    return js("try { return window.sessionStorage.getItem(key); } catch(e) { return null; }") as? String
}

/**
 * Set an item in SessionStorage
 * @param key The key to set
 * @param value The value to store (will be converted to string)
 */
fun setSessionItem(key: String, value: String) {
    js("try { window.sessionStorage.setItem(key, value); } catch(e) { console.error('SessionStorage setItem error:', e); }")
}

/**
 * Remove an item from SessionStorage
 * @param key The key to remove
 */
fun removeSessionItem(key: String) {
    js("try { window.sessionStorage.removeItem(key); } catch(e) { console.error('SessionStorage removeItem error:', e); }")
}

/**
 * Clear all items from SessionStorage
 * Used after OIDC flow completion to clean up temporary data
 */
fun clearSessionStorage() {
    js("try { window.sessionStorage.clear(); } catch(e) { console.error('SessionStorage clear error:', e); }")
}

/**
 * Get all items from SessionStorage (for debugging)
 * @return A map of all key-value pairs
 */
fun getAllSessionItems(): Map<String, String> {
    val items = mutableMapOf<String, String>()
    try {
        @Suppress("UnsafeDynamicallyTypedCall")
        val length = js("window.sessionStorage.length") as Int
        for (i in 0 until length) {
            @Suppress("UnsafeDynamicallyTypedCall")
            val key = js("window.sessionStorage.key(i)") as? String
            if (key != null) {
                @Suppress("UnsafeDynamicallyTypedCall")
                val value = js("window.sessionStorage.getItem(key)") as? String
                items[key] = value ?: ""
            }
        }
    } catch (e: Exception) {
        console.error("SessionStorage getAll error:", e)
    }
    return items
}
